import { CustomRoleId, RoboflowUser, Role, Workspace } from "../state/types";
import isUniverse from "./utils/isUniverse";
import _ from "lodash";

type User = RoboflowUser & { isAdmin?: boolean; admin?: boolean };
// TODO: rename permissions according to [1]+[2] (after finishing the miro map)
// TODO: routes shouldn't *be* actions. They should be *mapped to* actions.
// [1] https://docs.google.com/document/d/1p3CmbQR0g2FDr59g_VobvM-_cL2lkoGUheT9JTwCh_I/edit#heading=h.k65shep9r43v

// [2] https://miro.com/app/board/uXjVNbzLizM=/?share_link_id=652097538561
const EVERYONE_ACTIONS_PORTED_FROM_SERVERSIDE = ["accept_invite", "ask_to_join_workspace"];
const EVERYONE_ACTIONS = EVERYONE_ACTIONS_PORTED_FROM_SERVERSIDE;

const LABELER_ACTIONS_PORTED_FROM_SERVERSIDE = [
    "delete_annotation",
    "get_source",
    "list_project_groups",
    "read_models",
    "search_images",
    "search_workspace_images",
    "update_annotation",
    "submit_for_review",
    "update_label"
];
export const LABELER_ACTIONS = EVERYONE_ACTIONS_PORTED_FROM_SERVERSIDE.concat([
    "dataset.overview",
    "dataset.unassigned",
    "dataset.annotate",
    "dataset.overview.view",
    "create_workspace",
    "view_user_settings",
    "view_resources_sidebar",
    "view_projects",
    "update_tags",
    ...LABELER_ACTIONS_PORTED_FROM_SERVERSIDE
]);
const REVIEWER_ACTIONS = LABELER_ACTIONS.concat([
    "review_image",
    "change_job_settings",
    "view_all_jobs"
]);
const OWNER_ACTIONS_PORTED_FROM_SERVERSIDE = [
    "cancel_invite",
    "change_project_user_ownership",
    "change_user_role",
    "create_api_key",
    "update_api_key",
    "dataset_download_token",
    "delete_batch",
    "delete_dataset",
    "delete_job",
    "delete_setup_data",
    "delete_version",
    "edit_third_party_keys",
    "get_access_token",
    "get_api_key",
    "get_transform",
    "get_weights",
    "respond_to_join_request",
    "list_sources",
    "list_transforms",
    "merge_dataset",
    "change_split",
    "view_predictions",
    "remap_classes",
    "remap_tags",
    "remove_image",
    "remove_user",
    "rename_workspace",
    "revoke_api_key",
    "setup_data",
    "setup_plan",
    "train_model",
    "update_split",
    "view_stats",
    "star_project",
    "download_snap_weights",
    "create_job",
    "get_overages",
    "share_workflows",
    "create_dataset",
    "batch_processing_create_job",
    "batch_processing_download_data",
    "get_workspace_tags",
    "search_dataset_images",
    "view_workspace_api_keys",
    "trash_dataset",
    "restore_dataset",
    "trash_version",
    "restore_version",
    "trash_workflow",
    "restore_workflow",
    "view_trash"
];
export const OWNER_ACTIONS = REVIEWER_ACTIONS.concat([
    "invite_user",
    "edit_image_settings",
    "edit_overview",
    "upload_images",
    "assign_move_jobs",
    "auto_outsourced_labeling",
    "job_advanced_actions",
    "list_versions",
    "generate_version",
    "edit_dataset_sharing_configuration",
    "visualize_version",
    "deploy_version",
    "view_workspace_invites",
    "view_deploy_docs",
    "view_workspace_settings",
    "delete_workspace",
    "merge_workspace",
    "dataset.overview.edit",
    "dataset.create",
    "dataset.browse",
    "dataset.upload",
    "dataset.delete",
    "dataset.image.remove",
    "dataset.healthcheck",
    "dataset.active_learning",
    "dataset.settings.edit",
    "dataset.classes.edit",
    "dataset.rename",
    "download_weights",
    "manageSourceVideo",
    "update_dataset_skeleton",
    "manage_workflows",
    "publish_workflows",
    "manage_workflow_folders",
    "modelMonitoring",
    "manage_billing",
    "create_project_group",
    "create_top_level_project_group",
    "viewModelMonitoring",
    "view_models_page",
    "view_device_monitoring",
    "manage_devices",
    "view_billing_portal",
    "manage_usage_calculations",
    "use_inference_widget",
    "enable_join_requests",
    "change_review_mode",
    "manage_folder_permissions",
    "edit_workspace_settings",
    "view_audit_logs",
    "manage_siem_config",
    "read_credential",
    "write_credential",
    "read_bucket_mirror_config",
    "write_bucket_mirror_config",
    ...OWNER_ACTIONS_PORTED_FROM_SERVERSIDE
]);

// Super User permissions that should not be granted to standard roles (Owner, Labeler, Reviewer) by default
// These permissions are only available through custom role assignment
export const SUPER_USER_ACTIONS = ["manage_custom_roles", "manage_workspace_blocks"];

// Permission only available to Roboflow Admins. It will not even being listed on UI
export const ROBOFLOW_ADMIN_ACTIONS = ["view_project_videos"];

// Owner-only permissions that should NOT inherit through custom role's defaultBaseRole.
// These permissions require the user to be an actual workspace owner, not just have a custom role
// with defaultBaseRole='owner'. This prevents custom role users from getting elevated permissions
// that aren't explicitly assignable in the custom roles UI.
const OWNER_ONLY_NO_CUSTOM_ROLE_INHERITANCE = [
    "create_top_level_project_group",
    "read_credential",
    "write_credential",
    "read_bucket_mirror_config",
    "write_bucket_mirror_config"
];

export const ALL_ACTIONS = [...OWNER_ACTIONS, ...SUPER_USER_ACTIONS];

// Permissions that can be granted to custom roles and should be shown in the UI.
export const GRANTABLE_ACTIONS = ALL_ACTIONS.filter(
    (action) => !OWNER_ONLY_NO_CUSTOM_ROLE_INHERITANCE.includes(action)
);

export const DISABLED_CONTRIBUTOR_ACTIONS = [
    "change_user_role",
    "edit_third_party_keys",
    "edit_workspace_settings",
    "manage_billing",
    "manage_siem_config",
    "publish_workflows",
    ...SUPER_USER_ACTIONS
];

// Contributor custom role actions.
export const CONTRIBUTOR_ACTIONS = GRANTABLE_ACTIONS.filter(
    (action) => !DISABLED_CONTRIBUTOR_ACTIONS.includes(action)
);

// I REALLY HOPE TO GET RID OF THIS. It is just a hack to get the SSO admin page to not show a bunch of actions that are not currently relevant to SSO users, because SSO custom role permissions only apply to client-side actions.
export const ALL_ACTIONS_MINUS_SERVERSIDE = [
    ...OWNER_ACTIONS.filter(
        (action) =>
            !EVERYONE_ACTIONS_PORTED_FROM_SERVERSIDE.includes(action) &&
            !LABELER_ACTIONS_PORTED_FROM_SERVERSIDE.includes(action) &&
            !OWNER_ACTIONS_PORTED_FROM_SERVERSIDE.includes(action)
    ),
    ...SUPER_USER_ACTIONS
];

export const NEED_CURRENT_PROJECT_GROUP_WRITE_ACCESS = [
    "visualize_version",
    "list_versions",
    "generate_version",
    "dataset.active_learning",
    "dataset.classes.edit",
    "dataset.create",
    "deploy_version",
    "view_deploy_docs",
    "edit_dataset_sharing_configuration"
];

export const DISABLED_ACTIONS_FOR_SSO_WORKSPACES = [
    // Normal email/link invite flow
    "invite_user",
    // User accepting invite through link
    "accept_invite",
    // Discover & ask to join workspace flow
    "ask_to_join_workspace",
    "respond_to_join_request",
    // Share workflows
    "share_workflows"
];

export const ALWAYS_ALLOW_IN_UNIVERSE = ["view_deploy_docs", "use_inference_widget"];

export const JD_ONLY_ADMINS_ACTIONS = [
    "edit_workspace_settings",
    "view_workspace_settings",
    "view_user_settings",
    "view_resources_sidebar",
    "create_workspace",
    "dataset.settings.edit",
    "dataset.create"
];
export const JD_DISABLED_ACTIONS = ["invite_user", "accept_invite"];

export const roleXaction: Record<Role, string[]> = {
    labeler: LABELER_ACTIONS,
    reviewer: REVIEWER_ACTIONS,
    owner: OWNER_ACTIONS,
    creator: OWNER_ACTIONS
};
export const roleXdisallowedActions: Record<Role, string[]> = {
    labeler: _.difference(OWNER_ACTIONS, LABELER_ACTIONS),
    reviewer: _.difference(OWNER_ACTIONS, REVIEWER_ACTIONS),
    owner: _.difference(OWNER_ACTIONS, OWNER_ACTIONS),
    creator: _.difference(OWNER_ACTIONS, OWNER_ACTIONS)
};

export const actionXrole: Record<string, Role[]> = _buildActionXRoleMap();
const KNOWN_ACTIONS = new Set<string>([...ALL_ACTIONS, ...ROBOFLOW_ADMIN_ACTIONS]);

function _buildActionXRoleMap() {
    const map = {};
    LABELER_ACTIONS.map((a) => {
        map[a] = ["labeler"];
    });
    REVIEWER_ACTIONS.map((a) => {
        map[a] = map[a] || [];
        map[a].push("reviewer");
    });
    OWNER_ACTIONS.map((a) => {
        map[a] = map[a] || [];
        map[a].push("owner");
    });
    return map;
}

interface HasPermissionOptions {
    action: string;
    user?: User & { workspaces?: Record<string, Role> };
    workspace?: Workspace;
}

function hasPermissionThroughCustomRoles({
    action,
    user,
    workspace
}: HasPermissionOptions): boolean | undefined {
    if (!user) return undefined;

    if (!workspace?.customRolesEnabled) return undefined;

    const customRoleIds: CustomRoleId[] | undefined = workspace?.userCustomRoles?.[user.uid];
    if (!customRoleIds || customRoleIds.length === 0) return undefined;

    // For owner-only permissions that can't be granted through custom roles,
    // if the user has any custom role assigned, they should NOT have this permission
    // (it's not inheritable and not settable in the UI)
    if (OWNER_ONLY_NO_CUSTOM_ROLE_INHERITANCE.includes(action)) {
        return false;
    }

    let permission: boolean | undefined = undefined;
    for (const customRoleId of customRoleIds) {
        const customRole = workspace?.customRoles?.[customRoleId];

        const defaultBaseRole = customRole?.defaultBaseRole;
        const baseRolePermission = defaultBaseRole
            ? roleXaction[defaultBaseRole].includes(action)
            : undefined;

        // Fallback to default base role permission if custom role permission is not defined
        const customPermission =
            typeof customRole?.customRolePermissions?.[action] === "boolean"
                ? customRole.customRolePermissions[action]
                : baseRolePermission;
        if (typeof customPermission === "boolean") {
            // If permission is already true from another role, don't override it
            permission = permission || customPermission;
        }
    }
    return permission;
}

export function isRoboflowAdmin(user: User | undefined): boolean {
    return user?.isAdmin === true || user?.admin === true;
}

export function getUserBaseRole(user: User, workspace: Workspace | undefined): Role | undefined {
    return workspace?.roles?.[user?.uid] || user.workspaces?.[workspace?.id || ""] || undefined;
}

export function hasPermission({ action, user, workspace }: HasPermissionOptions) {
    if (ALWAYS_ALLOW_IN_UNIVERSE.includes(action) && isUniverse()) {
        return true;
    }

    if (!KNOWN_ACTIONS.has(action)) {
        // eslint-disable-next-line no-console
        console.warn(`[permissions] Unknown action ID encountered: "${action}"`);
    }

    // Handle case where user is not logged in
    if (!user) {
        return false;
    }

    // Special case for view_project_videos - only Roboflow Admins can see this
    if (ROBOFLOW_ADMIN_ACTIONS.includes(action)) {
        return isRoboflowAdmin(user);
    }

    const baseRole = getUserBaseRole(user, workspace);

    if (!baseRole) {
        return EVERYONE_ACTIONS.includes(action);
    }

    // Check custom roles first before falling back to base role permissions
    const permissionThroughCustomRoles = hasPermissionThroughCustomRoles({
        action,
        user,
        workspace
    });
    if (typeof permissionThroughCustomRoles === "boolean") {
        return permissionThroughCustomRoles;
    }

    const rolesAllowed = actionXrole[action];
    if (!rolesAllowed) {
        const hasSuperUserFeatureEnabled =
            workspace?.customRolesEnabled && workspace?.superUserEnabled;
        const isOwner = baseRole === "owner";

        if (hasSuperUserFeatureEnabled) {
            return isOwner;
        }

        return !SUPER_USER_ACTIONS.includes(action) && isOwner;
    }

    return rolesAllowed.includes(baseRole);
}

export type PermissionGroup = {
    name: string;
    description: string;
    permissions: Array<{ action: string; description: string; label: string }>;
};

// Map aliases to their canonical names - aliases should not be shown in UI
export const PERMISSION_ALIASES_TO_CANONICAL: Record<string, string> = {
    create_dataset: "dataset.create",
    delete_dataset: "dataset.delete"
};

export function getPermissionLabel(action: string): string {
    const groups = getPermissionGroups();
    for (const group of groups) {
        const permission = group.permissions.find((p) => p.action === action);
        if (permission) {
            return permission.label;
        }
    }
    return action; // Fallback to action ID if label not found
}

export function getPermissionGroups(): PermissionGroup[] {
    return [
        {
            name: "Dataset Management",
            description: "Control access to datasets, images, and data operations",
            permissions: [
                {
                    action: "dataset.create",
                    description: "Create new datasets in the workspace",
                    label: "Dataset Create"
                },
                {
                    action: "dataset.delete",
                    description: "Delete datasets from the workspace",
                    label: "Dataset Delete"
                },
                {
                    action: "dataset.overview",
                    description: "View dataset overview pages",
                    label: "Dataset Overview"
                },
                {
                    action: "dataset.overview.view",
                    description: "View dataset overview information",
                    label: "Dataset Overview View"
                },
                {
                    action: "dataset.overview.edit",
                    description: "Edit dataset overview settings",
                    label: "Dataset Overview Edit"
                },
                {
                    action: "edit_overview",
                    description: "Edit dataset overview content",
                    label: "Edit Overview"
                },
                {
                    action: "dataset.browse",
                    description: "Browse and view dataset images",
                    label: "Dataset Browse"
                },
                {
                    action: "dataset.upload",
                    description: "Upload images to datasets",
                    label: "Dataset Upload"
                },
                {
                    action: "upload_images",
                    description: "Upload images to datasets",
                    label: "Upload Images"
                },
                {
                    action: "dataset.annotate",
                    description: "Annotate images in datasets",
                    label: "Dataset Annotate"
                },
                {
                    action: "dataset.unassigned",
                    description: "View unassigned images",
                    label: "Dataset Unassigned"
                },
                {
                    action: "dataset.image.remove",
                    description: "Remove images from datasets",
                    label: "Dataset Image Remove"
                },
                {
                    action: "dataset.healthcheck",
                    description: "Run dataset health checks",
                    label: "Dataset Healthcheck"
                },
                {
                    action: "dataset.active_learning",
                    description: "Access active learning features",
                    label: "Dataset Active Learning"
                },
                {
                    action: "dataset.settings.edit",
                    description: "Edit dataset settings",
                    label: "Dataset Settings Edit"
                },
                {
                    action: "dataset.classes.edit",
                    description: "Edit dataset classes and labels",
                    label: "Dataset Classes Edit"
                },
                {
                    action: "dataset.rename",
                    description: "Rename dataset",
                    label: "Dataset Rename"
                },
                {
                    action: "search_dataset_images",
                    description: "Search for images within datasets",
                    label: "Search Dataset Images"
                },
                {
                    action: "remove_image",
                    description: "Remove individual images",
                    label: "Remove Image"
                },
                { action: "update_tags", description: "Update image tags", label: "Update Tags" },
                {
                    action: "update_label",
                    description: "Update image labels",
                    label: "Update Label"
                }
            ]
        },
        {
            name: "Annotation",
            description: "Manage annotations, reviews, and labeling workflows",
            permissions: [
                {
                    action: "delete_annotation",
                    description: "Delete annotations from images",
                    label: "Delete Annotation"
                },
                {
                    action: "update_annotation",
                    description: "Update existing annotations",
                    label: "Update Annotation"
                },
                {
                    action: "submit_for_review",
                    description: "Submit annotation jobs for review",
                    label: "Submit For Review"
                },
                {
                    action: "review_image",
                    description: "Review and approve/reject annotated images",
                    label: "Review Image"
                },
                {
                    action: "change_review_mode",
                    description: "Change review mode settings",
                    label: "Change Review Mode"
                }
            ]
        },
        {
            name: "Model Management",
            description: "Control access to model training, deployment, and versions",
            permissions: [
                { action: "train_model", description: "Train new models", label: "Train Model" },
                {
                    action: "generate_version",
                    description: "Generate new model versions",
                    label: "Generate Version"
                },
                {
                    action: "list_versions",
                    description: "View list of model versions",
                    label: "List Versions"
                },
                {
                    action: "delete_version",
                    description: "Delete model versions",
                    label: "Delete Version"
                },
                {
                    action: "visualize_version",
                    description: "Visualize model version performance",
                    label: "Visualize Version"
                },
                {
                    action: "deploy_version",
                    description: "Deploy model versions",
                    label: "Deploy Version"
                },
                {
                    action: "view_deploy_docs",
                    description: "View deployment documentation",
                    label: "View Deploy Docs"
                },
                {
                    action: "read_models",
                    description: "Read and view model information",
                    label: "Read Models"
                },
                {
                    action: "download_weights",
                    description: "Download model weights",
                    label: "Download Weights"
                },
                {
                    action: "download_snap_weights",
                    description: "Download snapshot weights",
                    label: "Download Snap Weights"
                },
                {
                    action: "get_weights",
                    description: "Retrieve model weights",
                    label: "Get Weights"
                },
                {
                    action: "update_dataset_skeleton",
                    description: "Update dataset skeleton configuration",
                    label: "Update Dataset Skeleton"
                },
                {
                    action: "view_predictions",
                    description: "View model predictions",
                    label: "View Predictions"
                },
                {
                    action: "use_inference_widget",
                    description: "Use inference widget for testing",
                    label: "Use Inference Widget"
                },
                {
                    action: "view_models_page",
                    description: "View the workspace Models page",
                    label: "View Models Page"
                }
            ]
        },
        {
            name: "Jobs & Batch Processing",
            description: "Manage annotation jobs and batch operations",
            permissions: [
                {
                    action: "create_job",
                    description: "Create new annotation jobs",
                    label: "Create Job"
                },
                {
                    action: "delete_job",
                    description: "Delete annotation jobs",
                    label: "Delete Job"
                },
                {
                    action: "view_all_jobs",
                    description: "View all annotation jobs",
                    label: "View All Jobs"
                },
                {
                    action: "change_job_settings",
                    description: "Change job settings and configuration",
                    label: "Change Job Settings"
                },
                {
                    action: "assign_move_jobs",
                    description: "Assign and move jobs between team members",
                    label: "Assign Move Jobs"
                },
                {
                    action: "job_advanced_actions",
                    description: "Perform advanced job actions",
                    label: "Job Advanced Actions"
                },
                {
                    action: "auto_outsourced_labeling",
                    description: "Use auto-outsourced labeling features",
                    label: "Auto Outsourced Labeling"
                },
                {
                    action: "batch_processing_create_job",
                    description: "Create batch processing jobs",
                    label: "Batch Processing Create Job"
                },
                {
                    action: "batch_processing_download_data",
                    description: "Download batch processing data",
                    label: "Batch Processing Download Data"
                },
                {
                    action: "delete_batch",
                    description: "Delete batch operations",
                    label: "Delete Batch"
                }
            ]
        },
        {
            name: "Workspace Management",
            description: "Control workspace settings, billing, and configuration",
            permissions: [
                {
                    action: "edit_workspace_settings",
                    description: "Edit workspace settings",
                    label: "Edit Workspace Settings"
                },
                {
                    action: "view_workspace_settings",
                    description: "View workspace settings",
                    label: "View Workspace Settings"
                },
                {
                    action: "rename_workspace",
                    description: "Rename the workspace",
                    label: "Rename Workspace"
                },
                {
                    action: "delete_workspace",
                    description: "Delete the workspace",
                    label: "Delete Workspace"
                },
                {
                    action: "merge_workspace",
                    description: "Merge workspaces",
                    label: "Merge Workspace"
                },
                {
                    action: "manage_billing",
                    description: "Manage workspace billing",
                    label: "Manage Billing"
                },
                {
                    action: "view_billing_portal",
                    description: "View billing portal",
                    label: "View Billing Portal"
                },
                {
                    action: "manage_usage_calculations",
                    description: "Create, edit, and delete saved usage calculations",
                    label: "Manage Usage Calculations"
                },
                { action: "setup_plan", description: "Set up workspace plan", label: "Setup Plan" },
                {
                    action: "get_overages",
                    description: "View usage overages",
                    label: "Get Overages"
                },
                {
                    action: "view_stats",
                    description: "View workspace statistics",
                    label: "View Stats"
                },
                {
                    action: "edit_third_party_keys",
                    description: "Edit third-party integration keys",
                    label: "Edit Third Party Keys"
                },
                {
                    action: "manage_custom_roles",
                    description: "Manage custom roles and permissions",
                    label: "Manage Custom Roles"
                },
                {
                    action: "manage_folder_permissions",
                    description: "Manage folder-level permissions",
                    label: "Manage Folder Permissions"
                },
                {
                    action: "manage_workspace_blocks",
                    description: "Manage Workflow Blocks Available to the Workspace",
                    label: "Manage Workspace Blocks"
                },
                {
                    action: "view_audit_logs",
                    description: "View workspace audit logs",
                    label: "View Audit Logs"
                },
                {
                    action: "manage_siem_config",
                    description: "Configure SIEM integration for audit log streaming",
                    label: "Manage SIEM Configuration"
                },
                {
                    action: "read_credential",
                    description: "View stored credentials",
                    label: "Read Credential"
                },
                {
                    action: "write_credential",
                    description: "Create, update, and delete credentials",
                    label: "Write Credential"
                },
                {
                    action: "read_bucket_mirror_config",
                    description: "View bucket mirror configuration",
                    label: "Read Bucket Mirror Config"
                },
                {
                    action: "write_bucket_mirror_config",
                    description: "Create, update, and delete bucket mirror configurations",
                    label: "Write Bucket Mirror Config"
                }
            ]
        },
        {
            name: "User & Team Management",
            description: "Control team member access and invitations",
            permissions: [
                {
                    action: "invite_user",
                    description: "Invite new users to the workspace",
                    label: "Invite User"
                },
                {
                    action: "view_workspace_invites",
                    description: "View workspace invitations",
                    label: "View Workspace Invites"
                },
                {
                    action: "cancel_invite",
                    description: "Cancel pending invitations",
                    label: "Cancel Invite"
                },
                {
                    action: "accept_invite",
                    description: "Accept workspace invitations",
                    label: "Accept Invite"
                },
                {
                    action: "ask_to_join_workspace",
                    description: "Request to join the workspace",
                    label: "Ask To Join Workspace"
                },
                {
                    action: "respond_to_join_request",
                    description: "Accept or deny join requests",
                    label: "Manage Invitation Request"
                },
                {
                    action: "enable_join_requests",
                    description: "Enable join request functionality",
                    label: "Enable Join Requests"
                },
                {
                    action: "remove_user",
                    description: "Remove users from the workspace",
                    label: "Remove User"
                },
                {
                    action: "change_user_role",
                    description: "Change user roles in the workspace",
                    label: "Change User Role"
                },
                {
                    action: "view_user_settings",
                    description: "View user settings",
                    label: "View User Settings"
                }
            ]
        },
        {
            name: "API & Integration",
            description: "Manage API keys and external integrations",
            permissions: [
                {
                    action: "create_api_key",
                    description: "Create new API keys",
                    label: "Create Api Key"
                },
                {
                    action: "update_api_key",
                    description: "Update existing API keys",
                    label: "Update Api Key"
                },
                {
                    action: "revoke_api_key",
                    description: "Revoke API keys",
                    label: "Revoke Api Key"
                },
                {
                    action: "get_api_key",
                    description: "Retrieve API key information",
                    label: "Get Api Key"
                },
                {
                    action: "get_access_token",
                    description: "Get access tokens",
                    label: "Get Access Token"
                },
                {
                    action: "dataset_download_token",
                    description: "Download dataset exports and generate download links",
                    label: "Dataset Download"
                },
                {
                    action: "view_workspace_api_keys",
                    description: "View workspace API keys",
                    label: "View Workspace API Keys"
                }
            ]
        },
        {
            name: "Projects & Folders",
            description: "Manage projects, folders, and organization",
            permissions: [
                {
                    action: "view_projects",
                    description: "View projects in the workspace",
                    label: "View Projects"
                },
                {
                    action: "star_project",
                    description: "Star and favorite projects",
                    label: "Star Project"
                },
                {
                    action: "create_project_group",
                    description: "Create project groups",
                    label: "Create Project Group"
                },
                {
                    action: "create_top_level_project_group",
                    description: "Create top-level project groups (root folders)",
                    label: "Create Top-Level Project Group"
                },
                {
                    action: "list_project_groups",
                    description: "List project groups",
                    label: "List Project Groups"
                },
                {
                    action: "edit_dataset_sharing_configuration",
                    description: "Edit dataset sharing configuration",
                    label: "Edit Dataset Sharing Configuration"
                },
                {
                    action: "change_project_user_ownership",
                    description: "Change the user owner of any project in this workspace",
                    label: "Change Project User Ownership"
                }
            ]
        },
        {
            name: "Workflows",
            description: "Manage automation workflows",
            permissions: [
                {
                    action: "manage_workflows",
                    description: "Manage workflow configurations",
                    label: "Manage Workflows"
                },
                {
                    action: "publish_workflows",
                    description: "Publish workflow changes to production",
                    label: "Publish Workflows"
                },
                {
                    action: "manage_workflow_folders",
                    description: "Manage workflow folder organization",
                    label: "Manage Workflow Folders"
                },
                {
                    action: "share_workflows",
                    description: "Share workflows with team members",
                    label: "Share Workflows"
                }
            ]
        },
        {
            name: "Sources & Transforms",
            description: "Manage data sources and transformations",
            permissions: [
                { action: "list_sources", description: "List data sources", label: "List Sources" },
                {
                    action: "get_source",
                    description: "Get source information",
                    label: "Get Source"
                },
                {
                    action: "list_transforms",
                    description: "List data transformations",
                    label: "List Transforms"
                },
                {
                    action: "get_transform",
                    description: "Get transformation details",
                    label: "Get Transform"
                },
                { action: "setup_data", description: "Set up data sources", label: "Setup Data" },
                {
                    action: "delete_setup_data",
                    description: "Delete data source configurations",
                    label: "Delete Setup Data"
                },
                {
                    action: "manageSourceVideo",
                    description: "Manage source video configurations",
                    label: "Manage Source Video"
                },
                {
                    action: "view_project_videos",
                    description: "View project videos",
                    label: "View Project Videos"
                }
            ]
        },
        {
            name: "Data Operations",
            description: "Perform data manipulation and analysis",
            permissions: [
                {
                    action: "merge_dataset",
                    description: "Merge datasets together",
                    label: "Merge Dataset"
                },
                {
                    action: "change_split",
                    description: "Change dataset train/val/test splits",
                    label: "Change Split"
                },
                {
                    action: "update_split",
                    description: "Update dataset splits",
                    label: "Update Split"
                },
                {
                    action: "remap_classes",
                    description: "Remap class labels",
                    label: "Remap Classes"
                },
                { action: "remap_tags", description: "Remap image tags", label: "Remap Tags" },
                {
                    action: "edit_image_settings",
                    description: "Edit image settings",
                    label: "Edit Image Settings"
                },
                {
                    action: "search_images",
                    description: "Search for images using CLIP semantic search",
                    label: "Search Images"
                },
                {
                    action: "search_workspace_images",
                    description: "Search and browse images in Asset Library",
                    label: "Search Workspace Images"
                },
                {
                    action: "get_workspace_tags",
                    description: "Get workspace tags",
                    label: "Get Workspace Tags"
                }
            ]
        },
        {
            name: "Monitoring & Analytics",
            description: "Access monitoring and analytics features",
            permissions: [
                {
                    action: "modelMonitoring",
                    description: "Access model monitoring features",
                    label: "Model Monitoring"
                },
                {
                    action: "viewModelMonitoring",
                    description: "View model monitoring dashboards",
                    label: "View Model Monitoring"
                }
            ]
        },
        {
            name: "Device Management",
            description: "Control access to edge device features",
            permissions: [
                {
                    action: "view_device_monitoring",
                    description: "View edge devices and device logs in the deployment manager",
                    label: "View Device Monitoring"
                },
                {
                    action: "manage_devices",
                    description:
                        "Create, update, delete, and configure edge devices, streams, groups, and device settings",
                    label: "Manage Devices"
                }
            ]
        },
        {
            name: "Trash Management",
            description: "Manage soft-deleted items and trash recovery",
            permissions: [
                {
                    action: "trash_dataset",
                    description: "Move datasets to trash",
                    label: "Trash Dataset"
                },
                {
                    action: "restore_dataset",
                    description: "Restore datasets from trash",
                    label: "Restore Dataset"
                },
                {
                    action: "trash_version",
                    description: "Move model versions to trash",
                    label: "Trash Version"
                },
                {
                    action: "restore_version",
                    description: "Restore model versions from trash",
                    label: "Restore Version"
                },
                {
                    action: "trash_workflow",
                    description: "Move workflows to trash",
                    label: "Trash Workflow"
                },
                {
                    action: "restore_workflow",
                    description: "Restore workflows from trash",
                    label: "Restore Workflow"
                },
                {
                    action: "view_trash",
                    description: "View trashed items in the workspace",
                    label: "View Trash"
                }
            ]
        },
        {
            name: "UI & Navigation",
            description: "Control UI element visibility",
            permissions: [
                {
                    action: "view_resources_sidebar",
                    description: "View resources sidebar",
                    label: "View Resources Sidebar"
                },
                {
                    action: "create_workspace",
                    description: "Create new workspaces",
                    label: "Create Workspace"
                }
            ]
        }
    ];
}
